// rootAlignment.js — map each letter of a derived root to its position in the
// surface word, so the UI can TEACH how a word grows from its root (e.g. show
// which letters of كِتاب are the radicals ك·ت·ب and which are pattern letters).
//
// Arabic morphology is non-concatenative: a root's radical consonants appear in
// order inside the word, interleaved with pattern letters (long/short vowels,
// prefixes, infixes, suffixes). Weak radicals (و ي ء and the hamza carriers) may
// surface as a different weak letter — e.g. root قول → قال (و→ا), رمي → رمى. We
// greedily align the root's consonants to the word's consonants left→right,
// allowing weak/alef/hamza letters to unify with one another.
//
// Pure (mirrors the char classes in rootDeriver.js).

import { foldLetterVariants } from "./arabicNormalize";

const HARAKAT = new Set("ًٌٍَُِّْٰ"); // tanween, harakat, shadda, sukun, dagger-alef
const TATWEEL = "ـ";

// Consonant letters (incl. the long vowels ا و ى ي and the hamza carriers).
const CONSONANTS = new Set(
  "ءآأؤإئاٱبةتثجحخدذرزسشصضطظعغفقكلمنهوىي".split("")
);

// The alef family + weak letters + hamza carriers — these interchange under the
// morphology, so a root radical from this set may surface as any other member.
const WEAK = new Set("ءآأؤإئاٱوىي".split(""));

const stripDiacritics = (s) =>
  [...(s || "")].filter((c) => !HARAKAT.has(c) && c !== TATWEEL).join("");

// Can a root radical `r` be realised by the surface consonant `w`?
const radicalMatches = (r, w) => {
  if (r === w) return true;
  // ة (taa marbuta) is a suffix marker, never a radical surface for ت here, but
  // treat ت/ة leniently since some stored roots use ت where the word shows ة.
  if ((r === "ت" && w === "ة") || (r === "ة" && w === "ت")) return true;
  return WEAK.has(r) && WEAK.has(w);
};

/**
 * Align a root to a surface word.
 *
 * @param {string} word  the surface word (harakat are stripped internally)
 * @param {string} root  the derived root (radical consonants, no harakat)
 * @returns {null | {
 *   letters: Array<{ char, role: 'radical'|'extra', radicalIndex: number }>,
 *   root: Array<{ char, radicalIndex, wordIndex }>,
 * }}  null if the root can't be aligned (caller falls back to a plain display).
 */
export const alignRootToWord = (word, root) => {
  // Persian/Urdu keyboard letters and presentation forms (e.g. کتاب typed with a
  // keheh) are shown as the standard letters, which the radicals can match.
  const wordChars = [...stripDiacritics(foldLetterVariants(word))];
  const rootChars = [...stripDiacritics(root)].filter((c) => CONSONANTS.has(c));
  if (!wordChars.length || rootChars.length < 2) return null;

  // Indices of the word's consonants (skip anything non-consonant just in case).
  const consPos = [];
  wordChars.forEach((c, i) => {
    if (CONSONANTS.has(c)) consPos.push(i);
  });

  // Greedy left→right subsequence alignment of root radicals onto word consonants.
  const radicalAt = new Array(wordChars.length).fill(-1); // wordIndex → radicalIndex
  const rootMap = []; // { char, radicalIndex, wordIndex }
  let cursor = 0; // index into consPos
  for (let ri = 0; ri < rootChars.length; ri++) {
    let placed = false;
    for (let k = cursor; k < consPos.length; k++) {
      const wi = consPos[k];
      if (radicalMatches(rootChars[ri], wordChars[wi])) {
        radicalAt[wi] = ri;
        rootMap.push({ char: rootChars[ri], radicalIndex: ri, wordIndex: wi });
        cursor = k + 1;
        placed = true;
        break;
      }
    }
    if (!placed) return null; // couldn't align this radical → give up cleanly
  }

  const letters = wordChars.map((char, i) => ({
    char,
    role: radicalAt[i] >= 0 ? "radical" : "extra",
    radicalIndex: radicalAt[i],
  }));

  return { letters, root: rootMap };
};
